// MessageBubble Component
// Individual message bubble with actions, metadata, and styling

import React from 'react';
import { Box, Typography, Avatar, Paper, Chip, IconButton, Checkbox } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Info, Reply, Check, DoneAll } from '@mui/icons-material';

import { formatMessageTime } from '../../utils/dateUtils';
import { getMessagingTheme } from '../../assets/theme/messagingTheme';
import { getTagTypeById, getTimelineMetaForCategory } from '../../constants/logTypeRegistry';
import MessageAttachments from './MessageAttachments';
import MessageLifelogAction from './MessageLifelogAction';

const renderChipIcon = (IconComponent, icon) => {
  if (IconComponent) {
    return <IconComponent sx={{ fontSize: 16, color: 'inherit' }} />;
  }

  if (icon) {
    return <span aria-hidden="true">{icon}</span>;
  }

  return null;
};

const MessageBubble = ({
  message,
  currentUserId,
  showAvatar = true,
  showTimestamp = true,
  onReply,
  onAddToLifelog,
  onRetryLifelog,
  lifelogNavigationState,
  canAddToLifelog = false,
  isAddingToLifelog = false,
  isAddedToLifelog = false,
  lifelogAddFailed = false,
  promotionState = null,
  selectionMode = false,
  isSelected = false,
  selectionDisabled = false,
  onToggleSelection,
}) => {
  const theme = useTheme();
  const messagingTheme = getMessagingTheme(theme);
  const isOwnMessage = message.senderId === currentUserId;
  const isRead = message.readBy && Object.keys(message.readBy).length > 1; // More than just sender
  const categoryMeta = message.category ? getTimelineMetaForCategory(message.category) : null;
  const selectionTime = message.createdAt ? formatMessageTime(message.createdAt) : 'unknown time';
  const messageTags = Array.isArray(message.tags) ? message.tags : [];
  const resolvedTags = messageTags
    .map((tagId) => {
      const tagType = getTagTypeById(tagId);
      if (!tagType) return null;
      return {
        id: tagId,
        label: tagType.label,
        icon: tagType.icon,
        iconComponent: tagType.iconComponent || null,
        meta: getTimelineMetaForCategory(tagType.category),
      };
    })
    .filter(Boolean);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-end',
        gap: 1,
        mb: 1,
        ...(isOwnMessage && {
          flexDirection: 'row-reverse',
        }),
      }}
    >
      {selectionMode && (
        <Checkbox
          checked={isSelected}
          disabled={selectionDisabled}
          onChange={() => onToggleSelection?.(message)}
          inputProps={{
            'aria-label': `Select message from ${message.senderName || 'Care Circle member'} at ${selectionTime}: ${String(message.text || 'attachment').trim().slice(0, 60)}`,
          }}
          sx={{ alignSelf: 'center' }}
        />
      )}
      {/* Avatar (only show for other users' messages) */}
      {showAvatar && !isOwnMessage && (
        <Avatar
          sx={{
            ...messagingTheme.childContext.avatar,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}
        >
          {message.senderName?.charAt(0)?.toUpperCase() || '?'}
        </Avatar>
      )}

      {/* Message content */}
      <Box
        sx={{
          maxWidth: { xs: '82%', md: '70%' },
          display: 'flex',
          flexDirection: 'column',
          alignItems: isOwnMessage ? 'flex-end' : 'flex-start',
        }}
      >
        {/* Sender name (only for other users' messages) */}
        {!isOwnMessage && showAvatar && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              mb: 0.5,
              ml: 0.5,
              fontSize: '0.82rem',
              fontWeight: 700,
            }}
          >
            {message.senderName}
          </Typography>
        )}

        {/* Reply indicator */}
        {message.replyTo && (
          <Box
            sx={{
              maxWidth: '100%',
              mb: 0.5,
              opacity: 0.7,
              borderLeft: 2,
              borderColor: 'primary.main',
              pl: 1,
              ...(isOwnMessage && {
                borderRight: 2,
                borderLeft: 0,
                pr: 1,
                pl: 0,
              }),
            }}
          >
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Replying to message
            </Typography>
          </Box>
        )}

        {/* Message bubble */}
        <Paper
          elevation={0}
          className="message-bubble"
          sx={{
            px: 2,
            py: 1.4,
            position: 'relative',
            maxWidth: '100%',
            wordBreak: 'break-word',
            boxShadow: '0 8px 20px rgba(15, 23, 42, 0.08)',
            transition: messagingTheme.transitions.fast,
            ...(isOwnMessage
              ? {
                  ...messagingTheme.messageBubble.own,
                  background: 'linear-gradient(135deg, #E6F7F3 0%, #D8F2EC 100%)',
                  color: theme.palette.text.primary,
                  border: '1px solid rgba(153, 211, 198, 0.35)',
                }
              : {
                  ...messagingTheme.messageBubble.other,
                  background: theme.palette.background.paper,
                  border: `1px solid rgba(226, 232, 240, 0.96)`,
                }),
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: '0 12px 28px rgba(15, 23, 42, 0.1)',
            },
          }}
        >
          {/* Message text */}
          <Typography
            variant="body2"
            sx={{
              whiteSpace: 'pre-wrap',
              lineHeight: 1.4,
            }}
          >
            {message.text}
          </Typography>
          <MessageAttachments attachments={message.attachments} />

          {/* Message metadata */}
          {message.metadata && message.metadata.incidentId && (
            <Chip
              size="small"
              label="Important Moment Shared"
              icon={<Info />}
              sx={{
                mt: 1,
                height: 24,
                fontSize: '0.7rem',
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: 'inherit',
              }}
            />
          )}

          {(categoryMeta || resolvedTags.length > 0) && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.5,
                mt: 1,
              }}
            >
              {categoryMeta && message.category !== 'log' && (
                <Chip
                  size="small"
                  label={categoryMeta.label}
                  icon={renderChipIcon(categoryMeta.iconComponent, categoryMeta.icon)}
                  sx={{
                    height: 24,
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    bgcolor: isOwnMessage ? 'rgba(255,255,255,0.16)' : `${categoryMeta.color}18`,
                    color: isOwnMessage ? 'white' : categoryMeta.color,
                    border: isOwnMessage ? '1px solid rgba(255,255,255,0.18)' : `1px solid ${categoryMeta.color}33`,
                    '& .MuiChip-icon': {
                      color: 'inherit',
                    },
                  }}
                />
              )}

              {resolvedTags.map((tag) => (
                <Chip
                  key={tag.id}
                  size="small"
                  label={tag.label}
                  icon={renderChipIcon(tag.iconComponent || tag.meta?.iconComponent, tag.icon)}
                  sx={{
                    height: 24,
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    bgcolor: isOwnMessage ? 'rgba(255,255,255,0.16)' : `${tag.meta.color}18`,
                    color: isOwnMessage ? 'white' : tag.meta.color,
                    border: isOwnMessage ? '1px solid rgba(255,255,255,0.18)' : `1px solid ${tag.meta.color}33`,
                    '& .MuiChip-icon': {
                      color: 'inherit',
                    },
                  }}
                />
              ))}
            </Box>
          )}

          {/* Edited indicator */}
          {message.isEdited && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 0.5,
                opacity: 0.7,
                fontStyle: 'italic',
                fontSize: '0.7rem',
              }}
            >
              edited
            </Typography>
          )}

          {canAddToLifelog && !selectionMode && (
            <MessageLifelogAction
              message={message}
              promotionState={promotionState}
              isAdding={isAddingToLifelog}
              isAdded={isAddedToLifelog}
              addFailed={lifelogAddFailed}
              onAdd={onAddToLifelog}
              onRetry={onRetryLifelog}
              navigationState={lifelogNavigationState}
              isOwnMessage={isOwnMessage}
            />
          )}

          {/* Message actions */}
          <Box
            sx={{
              position: 'absolute',
              top: -12,
              ...(isOwnMessage ? { left: -50 } : { right: -50 }),
              display: 'flex',
              gap: 0.5,
              opacity: 0,
              transition: messagingTheme.transitions.fast,
              background: 'rgba(255,255,255,0.9)',
              backdropFilter: 'blur(10px)',
              borderRadius: '20px',
              padding: '4px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              '.message-bubble:hover &': {
                opacity: 1,
              },
            }}
            className="message-actions"
          >
            <IconButton
              size="small"
              onClick={() => onReply?.(message)}
              sx={{
                width: 28,
                height: 28,
                backgroundColor: 'background.paper',
                border: `1px solid ${theme.palette.divider}`,
                '&:hover': {
                  backgroundColor: theme.palette.primary.main,
                  color: 'white',
                  transform: 'scale(1.1)',
                },
              }}
            >
              <Reply sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        </Paper>

        {/* Timestamp and read status */}
        {showTimestamp && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              mt: 0.5,
              ...(isOwnMessage && {
                flexDirection: 'row-reverse',
              }),
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'text.disabled',
                fontSize: '0.78rem',
              }}
            >
              {formatMessageTime(message.createdAt)}
            </Typography>

            {/* Read status for own messages */}
            {isOwnMessage && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {isRead ? (
                  <DoneAll sx={{ fontSize: 14, color: 'primary.main' }} />
                ) : (
                  <Check sx={{ fontSize: 14, color: 'text.disabled' }} />
                )}
              </Box>
            )}
          </Box>
        )}

      </Box>
    </Box>
  );
};

export default MessageBubble;
